import { Suspense, useEffect, useRef } from 'react';

import { TabPanel, TabPanelContainer } from '../TabPanel';
import { useTabPanelContext } from '../TabPanel/context';

import DefaultSkeleton from './MessageList/DefaultSkeleton';
import showUnreadNotification from './MessageList/MessageItem/UnreadNotification/utils';
import MessageList from './MessageList';

import useDebounceValue from 'modules/common/useDebounceValue';
import useZDUser from 'services/help-center/queries/useZDUser';
import { useMessageCenterContext } from 'modules/IMC/MessageCenter/context';
import { useIMCContext } from 'modules/IMC/common/context';
import { DrawerTypes } from 'modules/IMC/common/context/IMCContext.interface';
import { collectiveTracking } from 'modules/common/components/AnalyticsTracking/utils';
import useSearchTickets from 'services/help-center/queries/useSearchTickets';
import { CLOSED_MESSAGES_STATUSES, OPEN_MESSAGES_STATUSES } from 'services/help-center/constants';

export default function MessageCenterContent() {
    const { data: zdUser } = useZDUser();
    const { searchQuery } = useMessageCenterContext();
    const { setHasUnreadMessages, currOpenDrawer } = useIMCContext();
    const { currentTab } = useTabPanelContext();

    const debouncedSearchQuery = useDebounceValue<string>(searchQuery, searchQuery ? 800 : 0);

    // Use aggressive polling (5 minutes) when message center is open, otherwise 10 minutes
    const isMessageCenterOpen = currOpenDrawer === DrawerTypes.MESSAGE_CENTER;
    const refetchInterval = isMessageCenterOpen ? 1000 * 60 * 5 : 1000 * 60 * 10;

    const { data: openMessagesData, isLoading: openMessagesLoading } = useSearchTickets(
        zdUser?.id,
        debouncedSearchQuery,
        OPEN_MESSAGES_STATUSES,
        {
            enabled: currentTab === 'open',
            refetchInterval,
        }
    );

    const { data: closedMessagesData, isLoading: closedMessagesLoading } = useSearchTickets(
        zdUser?.id,
        debouncedSearchQuery,
        CLOSED_MESSAGES_STATUSES,
        {
            enabled: currentTab === 'closed',
            refetchInterval,
        }
    );

    const prevDebouncedQueryRef = useRef<string | null>(null);

    // Only the active tab's query runs, so a search from the closed tab never got open-tab data and
    // went untracked. Read whichever tab the search was made from.
    const activeTabMessagesData = currentTab === 'closed' ? closedMessagesData : openMessagesData;

    useEffect(() => {
        if (debouncedSearchQuery && debouncedSearchQuery !== prevDebouncedQueryRef.current && activeTabMessagesData) {
            collectiveTracking.track({
                name: 'Chat Searched',
                properties: {
                    category: 'message_center',
                    action: 'click',
                    search_query: debouncedSearchQuery,
                    timestamp: new Date().toISOString(),
                    userId: zdUser?.id,
                    results_count: activeTabMessagesData.results?.length || 0,
                    tab: currentTab,
                },
            });
            prevDebouncedQueryRef.current = debouncedSearchQuery;
        }
    }, [debouncedSearchQuery, zdUser?.id, activeTabMessagesData, currentTab]);

    useEffect(() => {
        const combinedTickets = [...(openMessagesData?.results || []), ...(closedMessagesData?.results || [])];
        if (!combinedTickets.length) {
            return;
        }

        const hasUnread = combinedTickets.some((ticket) => showUnreadNotification(ticket, zdUser));
        setHasUnreadMessages(hasUnread);
    }, [openMessagesData, closedMessagesData, setHasUnreadMessages, zdUser]);

    return (
        <TabPanelContainer>
            <TabPanel name="open">
                <Suspense fallback={<DefaultSkeleton />}>
                    <MessageList
                        status={OPEN_MESSAGES_STATUSES}
                        tickets={openMessagesData?.results || []}
                        isLoading={openMessagesLoading}
                    />
                </Suspense>
            </TabPanel>
            <TabPanel name="closed">
                <Suspense fallback={<DefaultSkeleton />}>
                    <MessageList
                        status={CLOSED_MESSAGES_STATUSES}
                        tickets={closedMessagesData?.results || []}
                        isLoading={closedMessagesLoading}
                    />
                </Suspense>
            </TabPanel>
        </TabPanelContainer>
    );
}
